'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

type RecorderStatus = 'idle' | 'recording' | 'uploading' | 'done' | 'error'

interface UseScreenRecorderReturn {
  status: RecorderStatus
  error: string | null
  isDesktop: boolean
  startRecording: () => Promise<void>
  stopAndUpload: () => Promise<string | null>
}

const isDesktop =
  typeof window !== 'undefined' && window.innerWidth >= 1024

export function useScreenRecorder(): UseScreenRecorderReturn {
  const [status, setStatus] = useState<RecorderStatus>('idle')
  const [error, setError] = useState<string | null>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])

  // beforeunload guard while recording
  useEffect(() => {
    if (status !== 'recording') return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [status])

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      })

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : 'video/webm'

      chunksRef.current = []
      const recorder = new MediaRecorder(stream, { mimeType })

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      streamRef.current = stream
      recorderRef.current = recorder

      recorder.start(5000)
      setStatus('recording')
      setError(null)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start recording'
      setError(msg)
      setStatus('error')
    }
  }, [])

  const stopAndUpload = useCallback(async (): Promise<string | null> => {
    const recorder = recorderRef.current
    const stream = streamRef.current

    if (!recorder || recorder.state === 'inactive') {
      stream?.getTracks().forEach((t) => t.stop())
      return null
    }

    setStatus('uploading')

    // Collect final chunk and stop
    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve()
      recorder.stop()
    })

    stream?.getTracks().forEach((t) => t.stop())

    const mimeType = recorder.mimeType || 'video/webm'
    const blob = new Blob(chunksRef.current, { type: mimeType })
    const filename = `kolleno-${Date.now()}-${Math.random().toString(36).slice(2)}.webm`

    try {
      const urlRes = await fetch('/api/kolleno/get-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename, contentType: mimeType }),
      })

      if (!urlRes.ok) {
        setStatus('done')
        return null
      }

      const { uploadUrl, publicUrl } = await urlRes.json()

      // Long screen recordings are large (tens of MB) and a single transient
      // network hiccup used to silently lose them. Retry with backoff, and if
      // every attempt fails, report it to the backend so a lost recording is
      // visible rather than a silent null.
      const backoffMs = [0, 1000, 3000, 6000]
      let lastErr = ''
      for (let attempt = 0; attempt < backoffMs.length; attempt++) {
        if (backoffMs[attempt]) await new Promise((r) => setTimeout(r, backoffMs[attempt]))
        try {
          const res = await fetch(uploadUrl, {
            method: 'PUT',
            body: blob,
            headers: { 'Content-Type': mimeType },
          })
          if (res.ok) {
            setStatus('done')
            return publicUrl
          }
          lastErr = `HTTP ${res.status}`
        } catch (e) {
          lastErr = e instanceof Error ? e.message : 'network error'
        }
      }
      // All attempts failed — surface it (best-effort, never throws).
      fetch('/api/log-client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          error: `[screen-recording] upload failed after retries: ${lastErr}`,
          context: { sizeBytes: blob.size, filename },
        }),
      }).catch(() => {})
    } catch (err) {
      console.error('[useScreenRecorder] upload failed:', err)
    }

    setStatus('done')
    return null
  }, [])

  return { status, error, isDesktop, startRecording, stopAndUpload }
}
